import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { SearchableSelect } from "@/components/ui/searchable-select";
import type { SearchableSelectOption } from "@/components/ui/searchable-select";
import { useGroups } from "@/hooks/useGroups";
import { AttendanceChildSelect } from "./AttendanceChildSelect";
import { useChildcares } from "@/hooks/settings/useChildcare";
import { getActivityTypeLabels } from "@/types/activities";
import type { Childcare } from "@/types/organisation";

/** Sentinel value for the "All …" option (maps back to `undefined`). */
const ALL = "__all__";

export interface AttendanceFilters {
  date_from?: Date;
  date_to?: Date;
  child_id?: string;
  type?: string;
  group_id?: string;
  childcare_id?: string;
}

export interface AttendanceFilterBarProps {
  filters: AttendanceFilters;
  onChange: (partial: Partial<AttendanceFilters>) => void;
  onClear: () => void;
}

/**
 * Server-side filter bar for the Activities tab. All state is controlled by the
 * parent (persisted in URL search params); this component only renders the
 * controls and emits changes up. Built from existing UI primitives — no raw
 * `<form>` / `<table>`.
 */
export function AttendanceFilterBar({
  filters,
  onChange,
  onClear,
}: AttendanceFilterBarProps) {
  const { i18n } = useLingui();
  const { data: groups } = useGroups();
  // Childcare names for the dropdown labels (the group list only carries
  // childcare_id, not a human-readable name).
  const { data: childcaresPage } = useChildcares({ limit: 100 });

  const childcareNameById = useMemo(() => {
    const map = new Map<string, string>();
    const list = (childcaresPage?.data ?? []) as Childcare[];
    for (const cc of list) {
      if (cc.id) map.set(cc.id, cc.name ?? cc.id);
    }
    return map;
  }, [childcaresPage]);

  const activityTypeOptions = useMemo<SearchableSelectOption[]>(() => {
    const labels = getActivityTypeLabels(i18n);
    return Object.entries(labels).map(([value, label]) => ({ value, label }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.locale]);

  // Distinct childcares derived from the groups list (only childcares that
  // actually have groups are filterable), labelled with their human-readable
  // name and falling back to the id if the name has not loaded yet.
  const childcareOptions = useMemo<SearchableSelectOption[]>(() => {
    const seen = new Set<string>();
    const opts: SearchableSelectOption[] = [];
    for (const g of groups ?? []) {
      const id = g.childcare_id;
      if (id && !seen.has(id)) {
        seen.add(id);
        opts.push({ value: id, label: childcareNameById.get(id) ?? id });
      }
    }
    return opts;
  }, [groups, childcareNameById]);

  // Group options, narrowed to the selected childcare when one is active.
  const groupOptions = useMemo<SearchableSelectOption[]>(() => {
    return (groups ?? [])
      .filter((g) =>
        filters.childcare_id ? g.childcare_id === filters.childcare_id : true,
      )
      .map((g) => ({ value: g.id ?? "", label: g.name ?? "" }));
  }, [groups, filters.childcare_id]);

  const withAll = (
    options: SearchableSelectOption[],
    allLabel: string,
  ): SearchableSelectOption[] => [{ value: ALL, label: allLabel }, ...options];

  const emit = (key: keyof AttendanceFilters, raw: string) => {
    const value = raw === ALL || raw === "" ? undefined : raw;
    if (key === "childcare_id") {
      // Changing childcare clears an incompatible group selection.
      onChange({ childcare_id: value, group_id: undefined });
      return;
    }
    onChange({ [key]: value } as Partial<AttendanceFilters>);
  };

  const hasActiveFilters =
    !!filters.date_from ||
    !!filters.date_to ||
    !!filters.child_id ||
    !!filters.type ||
    !!filters.group_id ||
    !!filters.childcare_id;

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-card p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Date range */}
        <div className="lg:col-span-2">
          <DateRangePicker
            from={filters.date_from}
            to={filters.date_to}
            onSelect={(range) =>
              onChange({ date_from: range.from, date_to: range.to })
            }
            fromLabel={i18n._({
              id: "activities.filters.dateFrom",
              message: "From",
            })}
            toLabel={i18n._({ id: "activities.filters.dateTo", message: "To" })}
          />
        </div>

        {/* Child */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="filter-child" className="text-sm">
            {i18n._({ id: "activities.filters.child", message: "Child" })}
          </Label>
          <AttendanceChildSelect
            value={filters.child_id}
            onChange={(child_id) => onChange({ child_id })}
          />
        </div>

        {/* Activity type */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="filter-type" className="text-sm">
            {i18n._({ id: "activities.filters.type", message: "Type" })}
          </Label>
          <SearchableSelect
            id="filter-type"
            value={filters.type ?? ""}
            onChange={(v) => emit("type", v)}
            options={activityTypeOptions}
            placeholder={i18n._({
              id: "activities.filters.type",
              message: "Type",
            })}
            className="w-full"
          />
        </div>

        {/* Childcare */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="filter-childcare" className="text-sm">
            {i18n._({
              id: "activities.filters.childcare",
              message: "Childcare",
            })}
          </Label>
          <SearchableSelect
            id="filter-childcare"
            value={filters.childcare_id ?? ALL}
            onChange={(v) => emit("childcare_id", v)}
            options={withAll(
              childcareOptions,
              i18n._({
                id: "activities.filters.allChildcares",
                message: "All childcares",
              }),
            )}
            placeholder={i18n._({
              id: "activities.filters.allChildcares",
              message: "All childcares",
            })}
            className="w-full"
          />
        </div>

        {/* Group */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="filter-group" className="text-sm">
            {i18n._({ id: "activities.filters.group", message: "Group" })}
          </Label>
          <SearchableSelect
            id="filter-group"
            value={filters.group_id ?? ALL}
            onChange={(v) => emit("group_id", v)}
            options={withAll(
              groupOptions,
              i18n._({
                id: "activities.filters.allGroups",
                message: "All groups",
              }),
            )}
            placeholder={i18n._({
              id: "activities.filters.allGroups",
              message: "All groups",
            })}
            className="w-full"
          />
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            {i18n._({
              id: "activities.filters.clear",
              message: "Clear filters",
            })}
          </Button>
        </div>
      )}
    </div>
  );
}
